/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/accordion.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import './styles.scss'
import '../globals.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Link } from '@ta-interaktiv/contentbricks'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { Bar2 } from 'components/newBar'
import { Counter } from 'components/counter'
import { VaccAgeGroup } from 'components/vaccAgeGroup'
import { VaccAgeGroupCantons } from 'components/vaccAgeGroupCantons'
import { VaccCantons } from 'components/vaccCantons'
import { VaccInternational } from 'components/vaccInternational'
import { HospVaccChart } from 'components/hospVaccChart'
import { DeathVaccChart } from 'components/deathVaccChart'
import { Scrollchor } from 'react-scrollchor'
import { ArticleTeaserCarousel } from '../ArticleTeaserCarousel'

import * as d3 from 'd3'

// TODO Set the correct article ID here
const ARTICLE_ID = '708030607865'

export class AppComponent extends React.Component<Props, State> {
  constructor() {
    super()
    this.state = {
      totalVaccinations: 0,
      totalVaccinationsRounded: '0,0',
      percFullyVaccinated: 0,
      percFirstVaccinated: 0,
      percNotVaccinated: 0,
      percAtLeastOneDose: 0,
      percFirstBooster: 0,
      percAdultsFullyVaccinated: 0,
      percAdultsFirstVaccinated: 0,
      percAdultsNotVaccinated: 0,
      percAdultsAtLeastOneDose: 0,
      percAdultsFirstBooster: 0,
      perc65UpFullyVaccinated: 0,
      perc65UpFirstVaccinated: 0,
      perc65UpNotVaccinated: 0,
      perc65UpAtLeastOneDose: 0,
      perc65UpToGoal: 0,
      perc65UpRest: 0,
      perc65UpFirstBooster: 0,
      deliveries: new Map([
        ['pfizer_biontech', 0],
        ['moderna', 0]
      ]),
      percFirstDose20to39: 0,
      percFirstBooster20to39: 0,
      percFirstDose60to79: 0,
      percFirstBooster60to79: 0,
      minPercFullyCanton: 0,
      maxPercFullyCanton: 0,
      minPercFirstBoosterCanton: 0,
      maxPercFirstBoosterCanton: 0
    }
  }

  componentDidMount() {
    d3.csv(
      'https://ta-interaktiv.storage.googleapis.com/corona-dashboard/vacc-deliveries.csv',
      d3.autoType
    ).then(data => {
      const deliveries = d3.rollup(
        data,
        d => d[d.length - 1],
        d => d.vaccine
      )
      this.setState({ deliveries })
    })

    d3.csv(
      'https://storage.googleapis.com/ta-interaktiv/corona-dashboard/vacc-kantone.csv'
    ).then(data => {
      // console.log('### DATA ### ', data)

      // sum up all cantons to get total vaccinations in CH
      const totalVaccinations = data.reduce(
        (a, { dosesAdministered }) => a + parseInt(dosesAdministered),
        0
      )

      // get min max for percFullyVaccinated
      const minFully = data.reduce(function(prev, curr) {
        // console.log('prev', 100 + parseFloat(prev.percFullyVaccinated))
        return +parseFloat(prev.percFullyVaccinated) <
          +parseFloat(curr.percFullyVaccinated)
          ? prev
          : curr
      })
      const maxFully = data.reduce(function(prev, curr) {
        return +parseFloat(prev.percFullyVaccinated) >
          +parseFloat(curr.percFullyVaccinated)
          ? prev
          : curr
      })

      // get min max for percFirstBooster
      const minFirstBooster = data.reduce(function(prev, curr) {
        // console.log('prev', 100 + parseFloat(prev.percFirstBooster))
        return +parseFloat(prev.percFirstBooster) <
          +parseFloat(curr.percFirstBooster)
          ? prev
          : curr
      })
      const maxFirstBooster = data.reduce(function(prev, curr) {
        return +parseFloat(prev.percFirstBooster) >
          +parseFloat(curr.percFirstBooster)
          ? prev
          : curr
      })

      this.setState({
        totalVaccinations: totalVaccinations,
        totalVaccinationsRounded: String(
          (parseInt(totalVaccinations) / 1000000).toFixed(1)
        )
          .replace('.', ',')
          .replace(',0', ''),
        minPercFullyCanton: String(
          +parseFloat(minFully.percFullyVaccinated).toFixed(1)
        )
          .replace('.', ',')
          .replace(',0', ''),
        maxPercFullyCanton: String(
          +parseFloat(maxFully.percFullyVaccinated).toFixed(1)
        )
          .replace('.', ',')
          .replace(',0', ''),
        minPercFirstBoosterCanton: String(
          +parseFloat(minFirstBooster.percFirstBooster).toFixed(1)
        )
          .replace('.', ',')
          .replace(',0', ''),
        maxPercFirstBoosterCanton: String(
          +parseFloat(maxFirstBooster.percFirstBooster).toFixed(1)
        )
          .replace('.', ',')
          .replace(',0', '')
      })
    })

    this.fetchData()
  }

  fetchData() {
    d3.csv(
      'https://storage.googleapis.com/ta-interaktiv/corona-dashboard/bag_covid_19_data_csv/COVID19VaccPersons_v2.csv',
      d3.autoType
    ).then(data => {
      // console.log(data)

      // GET PERCENTAGES FOR TOTAL POPULATON
      const totalPopRaw = data.filter(
        d =>
          d.geoRegion &&
          d.geoRegion === 'CH' &&
          d.age_group === 'total_population'
      )
      // console.log(totalPopRaw)
      const totalPop = d3.rollup(
        totalPopRaw,
        d => d[d.length - 1],
        d => d.type
      )
      // console.log(totalPop)
      const percFullyVaccinated = +parseFloat(
        totalPop.get('COVID19FullyVaccPersons').per100PersonsTotal
      ).toFixed(1)
      const percFirstVaccinated = +parseFloat(
        totalPop.get('COVID19PartiallyVaccPersons').per100PersonsTotal
      ).toFixed(1)
      const percNotVaccinated =
        100 - (percFullyVaccinated + percFirstVaccinated)
      const percAtLeastOneDose = +parseFloat(
        totalPop.get('COVID19AtLeastOneDosePersons').per100PersonsTotal
      ).toFixed(1)
      const percFirstBooster = +parseFloat(
        totalPop.get('COVID19FirstBoosterPersons').per100PersonsTotal
      ).toFixed(1)
      this.setState({
        percFullyVaccinated: percFullyVaccinated,
        percFirstVaccinated: percFirstVaccinated,
        percNotVaccinated: percNotVaccinated,
        percAtLeastOneDose: percAtLeastOneDose,
        percFirstBooster: percFirstBooster
      })
      // console.log('percFirstBooster:', percFirstBooster)

      // GET PERCENTAGES FOR ADULTS 12+
      const adults12UpRaw = data.filter(
        d => d.geoRegion && d.geoRegion === 'CH' && d.age_group === '12+'
      )
      // console.log(adults12UpRaw)
      const adults12Up = d3.rollup(
        adults12UpRaw,
        d => d[d.length - 1],
        d => d.type
      )
      // console.log(adults12Up)
      const percAdultsFullyVaccinated = +parseFloat(
        adults12Up.get('COVID19FullyVaccPersons').per100PersonsTotal
      ).toFixed(1)
      const percAdultsFirstVaccinated = +parseFloat(
        adults12Up.get('COVID19PartiallyVaccPersons').per100PersonsTotal
      ).toFixed(1)
      const percAdultsNotVaccinated =
        100 - (percAdultsFullyVaccinated + percAdultsFirstVaccinated)
      const percAdultsAtLeastOneDose = +parseFloat(
        adults12Up.get('COVID19AtLeastOneDosePersons').per100PersonsTotal
      ).toFixed(1)
      const percAdultsFirstBooster = +parseFloat(
        adults12Up.get('COVID19FirstBoosterPersons').per100PersonsTotal
      ).toFixed(1)
      this.setState({
        percAdultsFullyVaccinated: percAdultsFullyVaccinated,
        percAdultsFirstVaccinated: percAdultsFirstVaccinated,
        percAdultsNotVaccinated: percAdultsNotVaccinated,
        percAdultsAtLeastOneDose: percAdultsAtLeastOneDose,
        percAdultsFirstBooster: percAdultsFirstBooster
      })
    })

    d3.csv(
      'https://storage.googleapis.com/ta-interaktiv/corona-dashboard/vacc-age-groups.csv',
      d3.autoType
    ).then(data => {
      // console.log(data)
      this.setState({
        percFirstDose20to39: String(
          +parseFloat(
            data.find(o => o.ageGroup === '20–39').COVID19AtLeastOneDosePersons
          ).toFixed(1)
        )
          .replace('.', ',')
          .replace(',0', ''),
        percFirstBooster20to39: String(
          +parseFloat(
            data.find(o => o.ageGroup === '20–39').COVID19FirstBoosterPersons
          ).toFixed(1)
        )
          .replace('.', ',')
          .replace(',0', ''),
        percFirstDose60to79: String(
          +parseFloat(
            data.find(o => o.ageGroup === '60–79').COVID19AtLeastOneDosePersons
          ).toFixed(1)
        )
          .replace('.', ',')
          .replace(',0', ''),
        percFirstBooster60to79: String(
          +parseFloat(
            data.find(o => o.ageGroup === '60–79').COVID19FirstBoosterPersons
          ).toFixed(1)
        )
          .replace('.', ',')
          .replace(',0', '')
      })
    })

    /*
    d3.csv(
      'https://storage.googleapis.com/ta-interaktiv/corona-dashboard/bag_covid_19_data_csv/COVID19VaccPersons_AKL10_w_v2.csv',
      d3.autoType
    ).then(data => {
      // console.log(data)

      // GET PERCENTAGES FOR ADULTS 65+
      const adults65UpRaw = data.filter(
        d =>
          d.geoRegion &&
          d.geoRegion === 'CH' &&
          d.altersklasse_covid19 === '65+'
      )
      // console.log(adults65UpRaw)
      const adults65Up = d3.rollup(
        adults65UpRaw,
        d => d[d.length - 1],
        d => d.type
      )
      // console.log(adults65Up)
      const perc65UpFullyVaccinated = +parseFloat(
        adults65Up.get('COVID19FullyVaccPersons').per100PersonsTotal
      ).toFixed(1)
      const perc65UpFirstVaccinated = +parseFloat(
        adults65Up.get('COVID19PartiallyVaccPersons').per100PersonsTotal
      ).toFixed(1)
      const perc65UpNotVaccinated =
        100 - (perc65UpFullyVaccinated + perc65UpFirstVaccinated)
      const perc65UpAtLeastOneDose = +parseFloat(
        adults65Up.get('COVID19AtLeastOneDosePersons').per100PersonsTotal
      ).toFixed(1)
      // console.log('perc65UpAtLeastOneDose: ', perc65UpAtLeastOneDose)
      const perc65UpToGoal =
        perc65UpAtLeastOneDose < 93 ? 93 - perc65UpAtLeastOneDose : 0
      const perc65UpRest =
        perc65UpAtLeastOneDose < 93 ? 100 - 93 : perc65UpNotVaccinated
      const perc65UpFirstBooster = +parseFloat(
        adults65Up.get('COVID19FirstBoosterPersons').per100PersonsTotal
      ).toFixed(1)
      this.setState({
        perc65UpFullyVaccinated: perc65UpFullyVaccinated,
        perc65UpFirstVaccinated: perc65UpFirstVaccinated,
        perc65UpNotVaccinated: perc65UpNotVaccinated,
        perc65UpAtLeastOneDose: perc65UpAtLeastOneDose,
        perc65UpToGoal: perc65UpToGoal,
        perc65UpRest: perc65UpRest,
        perc65UpFirstBooster: perc65UpFirstBooster
      })
    })
    */
  }

  render() {
    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} />
        <ThemeSwitch />

        <header className='ui vertical  very fitted  segment'>
          <ArticleTeaserCarousel articleIdToRemove={ARTICLE_ID} />
          <div className='ui container'>
            <div className='ui header'>
              <h1>So weit ist die Schweiz mit dem Impfen</h1>
              <h2>
                Wie hoch sind die Impfquoten in den Kantonen und weltweit?
                Welche Unterschiede gibt es zwischen Geimpften und Ungeimpften?
                Die Übersicht.
              </h2>
            </div>
          </div>
        </header>

        <div
          className={
            browserCheck.isMobile
              ? 'ui vertical segment'
              : 'ui vertical fitted segment'
          }
        >
          <div className='ui container' />

          <div className='ui text container'>
            <div className='info'>
              Bis auf Weiteres werden wir hier keine neuen Impfdaten
              publizieren.
            </div>
            <div className='ta metainfo'>
              <div className='ta author'>
                <nobr>Yannick Wiget</nobr>, <nobr>Mathias Lutz</nobr>,{' '}
                <nobr>Patrick Vögeli</nobr>, <nobr>Marc Brupbacher</nobr>,{' '}
                <nobr>Sebastian Broschinski</nobr>, <nobr>Andreas Moor</nobr>
              </div>
              <div className='ta updated'>Aktualisiert am 25. Oktober 2022</div>
            </div>
          </div>

          <div className='ui container'>
            <div className='ta text'>
              <br />
              <Counter totalVaccinations={this.state.totalVaccinations} />
            </div>
          </div>

          <div className='ui container'>
            <div className='ui stackable two column padded grid'>
              <div className='column'>
                <div className='ui text'>
                  <AutofitIFrame
                    showLoader
                    src='//datawrapper.dwcdn.net/UOHjk/'
                  />
                </div>
              </div>
              <div className='column'>
                <div className='ui text'>
                  <AutofitIFrame
                    showLoader
                    src='//datawrapper.dwcdn.net/L2Rk4/'
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={
            browserCheck.isMobile
              ? 'ui vertical segment'
              : 'ui vertical fitted segment'
          }
          style={{ marginTop: '0', paddingTop: '0' }}
        >
          <div className='ui text container'>
            <div className='icon-wrapper'>
              <img src={require('../../images/wirksamkeit.svg')} />
            </div>
            <h4 className='q ta text'>
              Wie hoch ist die Wirksamkeit der Impfung?
            </h4>
            <div className='a ta text'>
              <p>
                Sehr hoch. Das zeigt der Vergleich zwischen Geimpften und
                Ungeimpften deutlich. Letztere haben ein viel höheres Risiko,
                aufgrund einer Covid-Infektion hospitalisiert zu werden oder gar
                daran zu sterben. Zwischen Ungeimpften und Personen, die einen
                Booster erhalten haben, ist die Differenz noch grösser.
              </p>
            </div>
          </div>

          <div className='ui container' style={{ marginBottom: '1em' }}>
            <div className='ui stackable two column padded grid'>
              <div className='column'>
                <HospVaccChart />
              </div>
              <div className='column'>
                <DeathVaccChart />
              </div>
            </div>
          </div>

          <div className='ui text container'>
            <div className='a ta text'>
              <p>
                Die Zahl der Spitaleintritte und -Todesfälle hat mit der weniger
                krank machenden Omikron-Variante deutlich abgenommen. Trotzdem
                sind Ungeimpfte immer noch viel häufiger von einem schweren
                Covid-Verlauf betroffen als Geimpfte.
              </p>
            </div>
          </div>

          <div className='ui text container'>
            <div className='icon-wrapper'>
              <img src={require('../../images/bevoelkerung.svg')} />
            </div>
            <h4 className='q ta text'>
              Welcher Anteil der Bevölkerung wurde schon geimpft?
            </h4>
            <div className='a ta text'>
              <p>
                Gemäss{' '}
                <Link
                  href='https://www.covid19.admin.ch/de/vaccination/persons'
                  allowWordWrap
                >
                  Zahlen des Bundesamtes für Gesundheit
                </Link>{' '}
                wurden in der Schweiz (ohne Liechtenstein) bis jetzt
                {' ' + this.state.totalVaccinationsRounded + ' '}
                Millionen Dosen gegen das Coronavirus verabreicht. Damit wurden{' '}
                {' ' +
                  this.state.percAtLeastOneDose.toString().replace('.', ',') +
                  ' '}{' '}
                Prozent der Gesamtbevölkerung mindestens einmal geimpft und{' '}
                {this.state.percFirstBooster.toString().replace('.', ',')}{' '}
                Prozent schon dreifach.
              </p>
              <div style={{ margin: '1em 0' }}>
                <Bar2
                  title='Anteil geimpfter Personen der Schweizer Bevölkerung'
                  source='BAG'
                  segments={[
                    {
                      label: 'vollständig',
                      value: this.state.percFullyVaccinated,
                      color: '#2ca29f',
                      textColor: '#fff',
                      labelInside: true,
                      position: 'top right'
                    },
                    {
                      label: 'Booster',
                      value: this.state.percFirstBooster,
                      labelInside: false,
                      position: 'top left',
                      offset: 40,
                      isOverlay: true
                    },
                    {
                      label: 'einfach',
                      value: this.state.percFirstVaccinated,
                      color: '#95d0cf',
                      labelInside: false,
                      position: 'bottom left'
                    },
                    {
                      label: 'ungeimpft',
                      value: this.state.percNotVaccinated,
                      color: '#e0e0e0',
                      labelInside: false,
                      position: 'top left'
                    }
                  ]}
                />
              </div>
              <p>
                Mittlerweile wird die Impfung auch für Kinder ab 5 Jahren
                empfohlen. Lange war sie aber nur für über 12-Jährige
                zugelassen. Insgesamt gibt es knapp 7,6 Millionen Personen in
                der Schweiz, die älter als zwölf Jahre sind.
                {' ' +
                  String(this.state.percAdultsAtLeastOneDose).replace(
                    '.',
                    ','
                  ) +
                  ' '}
                Prozent von ihnen haben bislang mindestens eine Dosis erhalten.
              </p>

              <div style={{ margin: '1em 0' }}>
                <Bar2
                  title='Anteil geimpfter Personen ab 12 Jahren'
                  source='BAG'
                  segments={[
                    {
                      label: 'vollständig',
                      value: this.state.percAdultsFullyVaccinated,
                      color: '#2ca29f',
                      labelInside: true,
                      textColor: '#fff',
                      position: 'top right'
                    },
                    {
                      label: 'Booster',
                      value: this.state.percAdultsFirstBooster,
                      labelInside: false,
                      position: 'top left',
                      offset: 40,
                      isOverlay: true
                    },
                    {
                      label: 'einfach',
                      value: this.state.percAdultsFirstVaccinated,
                      color: '#95d0cf',
                      labelInside: false,
                      textColor: '#202346',
                      position: 'bottom left'
                    },
                    {
                      label: 'ungeimpft',
                      value: this.state.percAdultsNotVaccinated,
                      color: '#e0e0e0',
                      labelInside: false,
                      textColor: '#202346',
                      position: 'top left'
                    }
                  ]}
                />
              </div>

              <p>
                Zwischen den Altersklassen gibt es aber noch grosse
                Unterschiede: Von den 60- bis 79-Jährigen sind inzwischen{' '}
                {' ' + this.state.percFirstDose60to79 + ' '}
                Prozent mindestens einmal geimpft und{' '}
                {this.state.percFirstBooster60to79} Prozent dreifach, bei den
                20- bis 39-Jährigen sind es erst{' '}
                {this.state.percFirstDose20to39} respektive{' '}
                {this.state.percFirstBooster20to39} Prozent.
              </p>
            </div>
          </div>

          <div className='ui text container' style={{ marginBottom: '2em' }}>
            <VaccAgeGroup />
          </div>

          {/*
          <div className='ui text container' style={{ marginBottom: '2em' }}>
            <div className='a ta text'>
              <p>
                Um eine Überlastung der Spitäler zu vermeiden, hat der Bundesrat
                Impfziele definiert. Er geht davon aus, dass 93 Prozent der über
                65-Jährigen und 80 Prozent der 18- bis 65-Jährigen immunisiert
                sein müssen. Vor allem bei den Älteren ist eine hohe Quote
                wichtig, weil sie öfter hospitalisiert werden müssen als
                Jüngere.
              </p>
              <div style={{ margin: '1em 0' }}>
                <Bar2
                  title='Anteil geimpfter Personen ab 65 Jahren'
                  source='BAG'
                  segments={[
                    {
                      label: 'vollständig',
                      value: this.state.perc65UpFullyVaccinated,
                      color: '#2ca29f',
                      textColor: '#fff',
                      labelInside: true,
                      position: 'top right'
                    },
                    {
                      label: 'Booster',
                      value: this.state.perc65UpFirstBooster,
                      labelInside: false,
                      position: 'top left',
                      isOverlay: true
                    },
                    {
                      label: 'einfach',
                      value: this.state.perc65UpFirstVaccinated,
                      color: '#95d0cf',
                      labelInside: false,
                      textColor: '#202346',
                      position: 'bottom left'
                    },
                    {
                      label: 'zum Ziel',
                      value: this.state.perc65UpToGoal,
                      color: '#F1BE8D',
                      labelInside: false,
                      position: 'top left'
                    },
                    {
                      value: this.state.perc65UpRest,
                      color: '#e0e0e0'
                    }
                  ]}
                />
              </div>
              <p>
                Von den über 65-Jährigen in der Schweiz sind bis jetzt{' '}
                {' ' +
                  this.state.perc65UpAtLeastOneDose
                    .toString()
                    .replace('.', ',') +
                  ' '}{' '}
                Prozent mindestens einmal geimpft. Bis zum Ziel des Bundesrats
                fehlen also noch{' '}
                {' ' +
                  this.state.perc65UpToGoal
                    .toFixed(1)
                    .toString()
                    .replace('.', ',') +
                  ' '}{' '}
                Prozent.
              </p>
            </div>
          </div>
                */}

          <div className='ui text container'>
            <div className='icon-wrapper'>
              <img src={require('../../images/kantone.svg')} />
            </div>
            <h4 className='q ta text'>
              Wie hoch ist die Impfquote in den Kantonen?
            </h4>
            <div className='a ta text'>
              <p>
                Die Kantone sind unterschiedlich weit mit der Immunisierung
                ihrer Bevölkerung, wie die BAG-Zahlen zeigen. Die Quote der
                vollständig Geimpften reicht von
                {' ' + this.state.minPercFullyCanton + ' '} bis{' '}
                {' ' + this.state.maxPercFullyCanton + ' '}Prozent, bei Personen
                mit einem Booster beträgt sie{' '}
                {this.state.minPercFirstBoosterCanton} bis{' '}
                {this.state.maxPercFirstBoosterCanton} Prozent.
              </p>
            </div>
          </div>

          <div className='ui text container' style={{ marginBottom: '2em' }}>
            <VaccCantons />
          </div>

          <div className='ui text container'>
            <div className='a ta text'>
              <p>
                Der Vergleich offenbart, dass die Quote vor allem in ländlichen
                Kantonen tief ist. Denn beim Impfen gibt es einen
                Stadt-Land-Graben, wie wir{' '}
                <Scrollchor
                  to='#stadt-land'
                  className='cbLink cB'
                  animate={{ offset: -20, duration: 1200 }}
                >
                  weiter unten aufzeigen
                </Scrollchor>
                .
              </p>
            </div>
          </div>

          <div className='ui text container' style={{ marginBottom: '2em' }}>
            <VaccAgeGroupCantons />
          </div>

          <div className='ui text container'>
            <div className='a ta text' />
          </div>

          {/* <div
            className='ui text container'
            style={{ marginBottom: '2em', marginTop: '2em' }}
          >
            <AutofitIFrame showLoader src='//datawrapper.dwcdn.net/9knO9/' />
          </div> */}

          <div className='ui text container'>
            <div className='icon-wrapper'>
              <img src={require('../../images/international.svg')} />
            </div>
            <h4 className='q ta text'>
              Wie schneidet die Schweiz im internationalen Vergleich&nbsp;ab?
            </h4>
            <div className='a ta text'>
              <p>
                Die Schweiz ist beim Impfen nur Mittelmass. Die meisten
                europäischen Länder, die über ähnliche finanzielle und
                medizinische Möglichkeiten verfügen, sind weiter mit dem Impfen.
              </p>
            </div>
          </div>

          <div className='ui text container' style={{ marginBottom: '2em' }}>
            <VaccInternational />
          </div>

          {/*
          <div className='ui text container'>
            <div className='a ta text'>
              <p>
                Nachdem die Schweiz mal in den Top-10 der Rangliste vertreten
                war, ist sie inzwischen nach unten gefallen. Denn viele Länder
                sind momentan schneller beim Impfen.
              </p>
            </div>
          </div>

          <div className='ui text container' style={{ margin: '-2em 0 2em' }}>
            <AutofitIFrame showLoader src='https://interaktiv.tagesanzeiger.ch/2020/covid-laendervergleich-embed/embed.html?sel=CH%2CDE%2CFR%2CIT&show=vacc&' />
          </div>
          */}

          <div className='ui text container'>
            <div className='icon-wrapper'>
              <img src={require('../../images/impfstoff.svg')} />
            </div>
            <h4 className='q ta text'>
              Wie viel Impfstoff steht zur Verfügung?
            </h4>
            <div className='a ta text'>
              <p>
                Der Bund hat mit sechs Herstellern Verträge für gut 57 Millionen
                Impfstoffdosen abgeschlossen. Drei davon haben bisher eine
                Zulassung erhalten: Pfizer/BioNTech, Moderna und Janssen (gehört
                zum Gesundheitskonzern Johnson & Johnson). Sie liefern ihre
                Vakzine nicht auf einmal, sondern nach Verfügbarkeit in die
                Schweiz. Deshalb hat der Bund erst einen Teil aller Bestellungen
                erhalten.
              </p>
            </div>
          </div>

          <div className='ui text container' style={{ marginBottom: '2em' }}>
            <AutofitIFrame showLoader src='//datawrapper.dwcdn.net/BnDyo/' />
          </div>

          {/* alte Balkenchartversion bestellte Impfdosen */}

          {/* <div className='graphicTitle'>
              So viele der bestellten Dosen hat die Schweiz schon&nbsp;erhalten
            </div>
            {console.log('deliveries', this.state.deliveries)}
            <Bar2
              title='Biontech/Pfizer (DE/US): 6 Mio. Dosen bestellt'
              source='BAG'
              segments={[
                {
                  label: 'erhalten',
                  value: this.state.deliveries.get('pfizer_biontech')
                    .pctOrdered,
                  color: '#2ca29f',
                  labelInside: false,
                  textColor: '#202346',
                  position: 'top right'
                },

                {
                  label: 'ausstehend',
                  value:
                    100 -
                    this.state.deliveries.get('pfizer_biontech').pctOrdered,
                  color: '#e0e0e0',
                  textColor: '#202346',
                  labelInside: true
                }
              ]}
            />
            <Bar2
              title='Moderna (US): 13,5 Mio. Dosen bestellt'
              source='BAG'
              segments={[
                {
                  label: 'erhalten',
                  value: this.state.deliveries.get('moderna').pctOrdered,
                  color: '#2ca29f',
                  labelInside: false,
                  textColor: '#202346',
                  position: 'top right'
                },

                {
                  label: 'ausstehend',
                  value: 100 - this.state.deliveries.get('moderna').pctOrdered,
                  color: '#e0e0e0',
                  labelInside: true,
                  textColor: '#202346'
                }
              ]}
            />
            <Bar2
              title='Moderna II (US): 7 Mio. Dosen für 2022 bestellt'
              source='BAG'
              segments={[
                {
                  label: 'ausstehend',
                  value: 100,
                  color: '#e0e0e0',
                  labelInside: true,
                  textColor: '#202346'
                }
              ]}
            />
            <Bar2
              title='Astra-Zeneca (GB/SE): 5,3 Mio. Dosen bestellt'
              source='BAG'
              segments={[
                {
                  label: 'ausstehend',
                  value: 100,
                  color: '#e0e0e0',
                  labelInside: true,
                  textColor: '#202346'
                }
              ]}
            />
            <Bar2
              title='Novavax (US): 6 Mio. Dosen bestellt'
              source='BAG'
              segments={[
                {
                  label: 'ausstehend',
                  value: 100,
                  color: '#e0e0e0',
                  labelInside: true,
                  textColor: '#202346'
                }
              ]}
            />
            <Bar2
              title='Curevac (DE): 5 Mio. Dosen bestellt'
              source='BAG'
              segments={[
                {
                  label: 'ausstehend',
                  value: 100,
                  color: '#e0e0e0',
                  labelInside: true,
                  textColor: '#202346'
                }
              ]}
            /> */}
          <div className='ui text container'>
            <div className='a ta text'>
              <p>
                Der Bund hat sich für den Fall abgesichert, dass weitere
                Auffrischungsimpfungen nötig werden. Alleine bis im Juni 2022
                sollte er zusätzlich 7,6 Millionen Dosen von Moderna erhalten
                und 5,6 Millionen Dosen von Pfizer/BioNTech. Zudem hat er sich
                die Option für weitere Lieferungen für die Jahre 2022 und 2023
                gesichert.
              </p>
              <div
                className='ui text'
                style={{ marginBottom: '2em', marginTop: '2em' }}
              >
                <AutofitIFrame
                  showLoader
                  src='//datawrapper.dwcdn.net/RF8hw/'
                />
              </div>
              <p>
                Die Hersteller Astra-Zeneca, Novavax und Curevac haben für ihre
                Vakzine noch keine Zulassung erhalten. Dementsprechend haben sie
                der Schweiz auch noch keine Dosen geliefert.
              </p>
            </div>
          </div>

          <div className='ui text container'>
            <div className='icon-wrapper'>
              <img src={require('../../images/bereitschaft.svg')} />
            </div>
            <h4 className='q ta text'>
              Wer will sich eigentlich nicht impfen lassen?
            </h4>

            <div className='a ta text'>
              <p>
                Die Impfbereitschaft ist in der Schweiz im Vergleich zu anderen
                Ländern tief – und hat abgenommen. Fast 70 Prozent der
                Bevölkerung haben sich laut dem BAG doppelt impfen lassen, aber
                nur 41 Prozent ein drittes Mal. Laut einer{' '}
                <Link
                  href='https://sotomo.ch/site/wp-content/uploads/2022/02/10.-SRG-Corona-Monitor-Sotomo.pdf'
                  allowWordWrap
                >
                  Umfrage der Forschungsstelle Sotomo vom 17. Februar 2022
                </Link>{' '}
                ist die Bereitschaft vor allem bei den Jungen tief.
              </p>
            </div>
            <div style={{ marginBottom: '2em' }}>
              <AutofitIFrame showLoader src='//datawrapper.dwcdn.net/H3vWF/' />
            </div>

            <div className='a ta text'>
              <p>
                Viele, die sich keinen Booster geholt haben, fühlen sich schon
                genügend geschützt oder finden es übertrieben, so häufig zu
                impfen. Einige warten auf ein Vakzin, das der Omikron-Variante
                angepasst ist. Weit verbreitet sind immer noch Zweifel an der
                Wirksamkeit und Sicherheit der Impfung generell.
              </p>
            </div>

            <div style={{ marginBottom: '2em' }}>
              <AutofitIFrame showLoader src='//datawrapper.dwcdn.net/Dh8JP/' />
            </div>

            <div className='a ta text' id='stadt-land'>
              <p>
                Im ländlichen Raum ist die Skepsis gegenüber dem Impfen
                tendenziell weiter verbreitet als in den Städten. Das ergab die{' '}
                <Link
                  href='https://sotomo.ch/site/wp-content/uploads/2021/10/9.-SRG-SSR-Corona-Umfrage-Sotomo.pdf'
                  allowWordWrap
                >
                  Sotomo-Umfrage vom 29. Oktober 2021
                </Link>
                . Sie zeigte bereits, dass Jüngere eine tiefere Bereitschaft
                haben als Ältere. Deutliche Unterschiede gibt es zudem nach
                Einkommen und Parteipräferenz.
              </p>
            </div>

            <div style={{ marginBottom: '2em' }}>
              <AutofitIFrame showLoader src='//datawrapper.dwcdn.net/botRr/' />
            </div>

            <div className='a ta text'>
              <p>
                Menschen mit einem vergleichsweise tiefen Lohn sind skeptischer
                als Gutverdiener. Und die Impfbereitschaft ist auch stark mit
                der politischen Orientierung verbunden: Von der
                SVP-Anhängerschaft will sich die Hälfte grundsätzlich nicht
                impfen lassen. Die in den nationalkonservativen Milieus eher
                verbreitete Wissenschaftsskepsis spielt dabei eine wichtige
                Rolle.
              </p>
              <p>
                Der Hauptgrund für eine abwartende oder ablehnende Haltung zur
                Impfung ist die Angst vor schädlichen Folgen für Körper und
                Gesundheit. 78 Prozent der befragten Personen äusserten diese
                Befürchtung. Gleich viele haben das Gefühl, dass sie genügend
                Abwehrkräfte gegen das Virus hätten. Weitere wichtige Gründe
                sind Zweifel an der Wirksamkeit der Impfung und der Protest
                gegen eine Bevormundung.
              </p>
            </div>
            <div style={{ marginBottom: '2em' }}>
              <AutofitIFrame showLoader src='//datawrapper.dwcdn.net/kA386/' />
            </div>
            <div className='a ta text'>
              <p>
                Die geäusserten Bedenken sind allerdings unbegründet, wie{' '}
                <Link href='781685178732'>dieser Faktencheck</Link> zu den
                populärsten Behauptungen rund um das Coronavirus und die Impfung
                zeigt.
              </p>
            </div>
          </div>

          {/* <div className='ui text container'>
            <h4 className='q ta text'>
              Wie lange geht es, bis alle (Freiwilligen) geimpft sind?
            </h4>

            <div className='a ta text'>
              <p style={{ marginTop: '2em' }}>
                Wenn die Lieferungen der Hersteller so eintreffen wie geplant,
                würde die Schweiz im September genügend Dosen haben, um alle
                Erwachsenen doppelt zu impfen.
              </p>
              <div
                className='ui text'
                style={{ marginBottom: '2em', marginTop: '2em' }}
              >
                <AutofitIFrame showLoader src='//datawrapper.dwcdn.net/RF8hw/' />
              </div>
              <p>
                Nur wollen sich ja nicht alle immunisieren lassen. Der Bund
                hoffte beim Start der Kampagne Anfang Jahr auf eine
                Impfbereitschaft von 75 Prozent bei den Erwachsenen. Das
                entspricht 5,3 Millionen Menschen, für die es schon im August
                genügend Dosen hätte. Rechnet man mit dieser Anzahl, sind schon
                {' ' + this.state.percWillingAtLeastOneDose + ' '}Prozent
                mindestens einmal geimpft.
              </p>
              <div style={{ margin: '1em 0' }}>
                <Bar2
                  title='Anteil der geimpften Impfwilligen, bei 75% Impfbereitschaft'
                  source='BAG'
                  segments={[
                    {
                      label: 'vollständig',
                      value: this.state.percWillingFullyVaccinated,
                      color: '#2ca29f',
                      labelInside: true,
                      textColor: '#fff'
                    },
                    {
                      label: 'einfach',
                      value: this.state.percWillingFirstVaccinated,
                      color: '#95d0cf',
                      labelInside: false,
                      textColor: '#202346',
                      position: 'bottom left'
                    },
                    {
                      label: 'ungeimpft',
                      value: this.state.percWillingNotVaccinated,
                      color: '#e0e0e0',
                      labelInside: false,
                      textColor: '#202346',
                      position: 'top left'
                    }
                  ]}
                />
              </div>
              <p>
                Es zeichnet sich allerdings ab, dass die Schweiz nicht auf eine
                Impfquote von 75 Prozent der Erwachsenen kommen wird. Stand
                jetzt sind erst{' ' + this.state.percAdultsAtLeastOneDose + ' '}
                Prozent von ihnen mindestens einmal geimpft.
              </p>
            </div>
          </div> */}
        </div>

        <Footer
          articleId={ARTICLE_ID}
          questions={['Wie informativ war der Beitrag?']}
          credits={[
            [
              'Konzept und Umsetzung',
              'Marc Brupbacher, Yannick Wiget, Patrick Vögeli, Mathias Lutz, Sebastian Broschinski, Andreas Moor'
            ]
          ]}
        />
      </div>
    )
  }
}

// const QandA = ({ q }) => (
//   <div className='qa'>
//     <div className='question'>{q.q}</div>
//     <div className='answer'>{q.a}</div>
//   </div>
// )
